/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { CSSProperties, ReactNode } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { buildUrlWithCSelections } from '~/utils/navigation'

interface LinkProps {
  to?: string
  stateName?: string
  stateParams?: Record<string, any>
  options?: Record<string, any>
  title?: string
  className?: string
  target?: string
  onClick?: () => void
  children: ReactNode
  id?: string
  style?: CSSProperties
  isFromBreadcrumbs?: boolean
}

/**
 * Custom Link component that bridges AngularJS ui-router patterns with React Router
 * Maps stateName/stateParams to React Router paths
 */
export function Link({
  to,
  stateName,
  stateParams = {},
  options = {},
  title,
  className,
  target,
  onClick,
  children,
  id,
  style = {},
  isFromBreadcrumbs = false
}: LinkProps) {
  const navigate = useNavigate()

  // Map AngularJS state names to React Router paths
  const getRouteFromState = (name?: string, params: Record<string, any> = {}): string => {
    if (!name) return to || '#'

    // Map common state names to routes
    const stateMap: Record<string, (p: Record<string, any>) => string> = {
      browser: (p) => {
        const basePath = `/browser/${p.folder || p.id || ''}`
        const queryParams = new URLSearchParams()
        if (p.page) queryParams.append('page', String(p.page))
        if (p.type) queryParams.append('type', String(p.type))
        if (p.propertySearchFilter) queryParams.append('propertySearchFilter', String(p.propertySearchFilter))
        const queryString = queryParams.toString()
        return queryString ? `${basePath}?${queryString}` : basePath
      },
      'show-file': (p) => `/show-file/${p.fileId || p.id || ''}`,
      tasks: () => '/tasks',
      workflow: (p) => `/workflows/${p.workflowId || p.id || ''}`,
      'staging-Documents': () => '/staging/Documents',
      'staging-MyDocuments': () => '/staging/MyDocuments',
      reports: () => '/reports',
      settings: () => '/settings'
    }

    const routeBuilder = stateMap[name]
    if (routeBuilder) {
      return routeBuilder(params)
    }

    // Fallback: try to use stateName as path
    return to || `/${name.replace(/\./g, '/')}`
  }

  const routePath = to || getRouteFromState(stateName, stateParams)
  const href = buildUrlWithCSelections(routePath)

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow target="_blank" to work normally
    if (target === '_blank') {
      return
    }

    // Allow right-click / ctrl+click / cmd+click to use the native href
    if (event.metaKey || event.ctrlKey || event.button !== 0) return

    event.preventDefault()

    // Special handling for show-file route (prevent duplicate navigation)
    if (stateName === 'show-file') {
      if (!window.location?.href?.includes('/show-file')) {
        navigate(routePath, { state: options })
      }
      // Only call onClick if from breadcrumbs or if different params
      if (isFromBreadcrumbs || !isSameLocation(href)) {
        onClick?.()
      }
    } else {
      navigate(routePath, { state: options })
      onClick?.()
    }

    return false
  }

  const isSameLocation = (path: string): boolean => {
    return window.location.pathname === path
  }

  return (
    <a id={id} href={href} title={title} target={target} style={style} onClick={handleClick} className={className}>
      {children}
    </a>
  )
}

export default Link
